/*
 * @Author: Ryan
 * @Date: 2025-12-26
 * @Description: Testimonials section - Trusted by 100,000+ Teams Worldwide
 */
import React from 'react';

interface Testimonial {
  title: string;
  content: string;
  role: string;
  name: string;
}

const testimonials: Testimonial[] = [
  {
    title: 'Faster and Easier than Filming',
    content:
      'With FalcoCut, turning a script into a professional-quality video takes just minutes. No more setting up cameras, booking studios, or spending hours editing. The entire process is smooth, fast, and effortless, delivering high-quality videos every time.',
    role: 'Video Producer',
    name: 'John Miller',
  },
  {
    title: 'Engaging Lessons without Being on Camera',
    content:
      'As an educator, I always struggled with recording myself on video. FalcoCut lets me create lifelike avatar lessons that keep my students engaged. It saves me hours of preparation and helps me update my content whenever I need.',
    role: 'High School Teacher',
    name: 'Sarah Johnson',
  },
  {
    title: 'Do More with the Same Resources',
    content:
      "Our marketing team used to be limited by budget and time, but with FalcoCut, we can produce twice as much content without hiring extra staff. It feels like we've added a new team member who works instantly and never makes mistakes.",
    role: 'Marketing Manager',
    name: 'David Chen',
  },
  {
    title: 'High-Quality Videos without Extra Cost',
    content:
      'Outsourcing video production was both expensive and slow for us. With FalcoCut, I can create high-quality avatar videos in-house. The platform gives me full control, flexibility, and professional results at a fraction of the cost.',
    role: 'Content Strategist',
    name: 'Emily Rodriguez',
  },
  {
    title: 'Scale Content Creation in Minutes',
    content:
      "FalcoCut has completely transformed our content strategy. From product demos to training modules and social media campaigns, we can now create videos at scale in just minutes. It's a game-changer for growing our global reach.",
    role: 'Head of Growth',
    name: 'Michael Thompson',
  },
  {
    title: 'Bring Ideas to Life Instantly',
    content:
      "With FalcoCut, I can turn any idea into a polished video almost instantly. The avatars look natural, the voices sound authentic, and the whole process is so intuitive that I don't need a production team anymore. It's given me the freedom to experiment and create at scale.",
    role: 'Startup Founder',
    name: 'Olivia Parker',
  },
];

const StarRating: React.FC = () => (
  <div className="flex gap-1 mb-3 pad:mb-4">
    {[1, 2, 3, 4, 5].map((star) => (
      <svg
        key={star}
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10 1L12.39 6.26L18 7.27L14 11.14L14.76 17L10 14.27L5.24 17L6 11.14L2 7.27L7.61 6.26L10 1Z"
          fill="#FBBF24"
          stroke="#FBBF24"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ))}
  </div>
);

const TestimonialCard: React.FC<Testimonial> = ({ title, content, role, name }) => {
  return (
    <div className="bg-white rounded-2xl p-6 pad:p-8 h-full flex flex-col">
      <StarRating />
      <h3 className="text-lg pad:text-xl font-bold text-gray-900 mb-3 pad:mb-4">
        {title}
      </h3>
      <p className="text-sm pad:text-base text-gray-600 leading-relaxed flex-grow mb-4 pad:mb-6">
        {content}
      </p>
      <div>
        <p className="font-semibold text-gray-900 text-sm pad:text-base">{role}</p>
        <p className="text-gray-500 text-sm">{name}</p>
      </div>
    </div>
  );
};

const TestimonialsSection: React.FC = () => {
  return (
    <section className="w-full bg-[#F5F7FB] py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-10 pad:mb-14 sm:mb-16 leading-tight">
          Trusted by 100,000+ Teams Worldwide
        </h2>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 pad:grid-cols-2 sm:grid-cols-3 gap-4 pad:gap-6">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              title={testimonial.title}
              content={testimonial.content}
              role={testimonial.role}
              name={testimonial.name}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
